<template>
  <div
    class="samples-list-details-grid"
    data-tour-id="samples-list-details-grid"
  >
    <div v-if="!loaded" class="preloader-overlay">
      <div class="preloader-card">
        <f7-preloader></f7-preloader>
        <div class="preloader-text">
          {{ $t('loading') }}
        </div>
      </div>
    </div>
    <DxDataGrid
      :data-source="normalizedSamples"
      :show-borders="false"
      :show-column-lines="false"
      :allow-column-reordering="true"
      :allow-column-resizing="true"
      :column-auto-width="true"
      :show-column-headers="true"
      :show-colon="false"
      :remote-operations="true"
      :hover-state-enabled="false"
      :show-row-lines="false"
      :row-alternation-enabled="false"
      class="custom-grid"
      :no-data-text="$t('noDataText')"
      @content-ready="init"
    >
      <DxColumn
        :caption="$t('agronomySample.summary.sampleCode')"
        data-field="CodMuestra"
      />
      <DxColumn
        v-if="hasMultipleBudgetCodes"
        :caption="$t('samplesRegister.budget')"
        alignment="center"
        data-field="CodPresupuesto"
      />
      <DxColumn
        :caption="$t('description')"
        alignment="center"
        data-field="Muestra"
      />

      <DxColumn
        :caption="$t('agronomySample.summary.sampleType')"
        alignment="center"
        data-field="TipoMuestra"
      />
      <DxColumn
        :caption="$t('foodSamples.analysisType')"
        alignment="center"
        data-field="AnalisisTipoDisplay"
      />
      <DxColumn
        :caption="$t('samplesRegister.farm')"
        alignment="center"
        data-field="Finca"
      />
      <DxColumn
        :caption="$t('samplesRegister.plot')"
        alignment="center"
        data-field="Parcela"
      />
      <DxColumn
        :caption="$t('SamplesList.price')"
        alignment="center"
        data-field="PrecioAT"
        cell-template="priceTemplate"
      />
      <DxColumn
        :buttons="updatedEditButtons"
        :fixed="true"
        fixed-position="right"
        type="buttons"
        name="actionColumn"
      />
      <DxPager
        :allowed-page-sizes="[5, 10, 15, 20]"
        :show-page-size-selector="true"
        info-text="{2} items"
        :visible="true"
      />
      <DxPaging :page-size="5" />
      <template #priceTemplate="{ data }">
        <span>
          {{
            formatAmount(
              data.data.PrecioAT,
              getSampleCurrency(data.data)
            )
          }}
        </span>
      </template>
    </DxDataGrid>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import helpers from '@/js/helpers';
import {
  DxDataGrid,
  DxColumn,
  DxPager,
  DxPaging,
} from 'devextreme-vue/data-grid';
import { NOTIFY_TIME_LONG } from '@/js/constants';
import iconPencil from '@/static/icons/ic_pencil.svg';
import iconTrash from '@/static/icons/ic_trash_new.svg';

export default {
  components: {
    DxDataGrid,
    DxColumn,
    DxPager,
    DxPaging,
  },
  props: {
    store: {
      type: Array,
      default: () => [],
    },
    currency: {
      type: String,
      default: 'EUR',
    },
  },
  data() {
    return {
      loaded: false,
      precioTotal: 0,
      PrecioAT: 0,
      enrichedSamples: [],
    };
  },
  computed: {
    normalizedSamples() {
      const sourceSamples =
        this.enrichedSamples.length > 0
          ? this.enrichedSamples
          : Array.isArray(this.store)
          ? this.store
          : [];

      return sourceSamples.map((sample) => ({
        ...sample,
        AnalisisTipoDisplay: this.buildAnalysisDisplayText(sample),
      }));
    },
    hasMultipleBudgetCodes() {
      const budgetCodes = new Set(
        (Array.isArray(this.store) ? this.store : [])
          .map((sample) => sample?.CodPresupuesto)
          .filter(
            (budgetCode) =>
              budgetCode !== null &&
              budgetCode !== undefined &&
              String(budgetCode).trim() !== ''
          )
      );

      return budgetCodes.size > 1;
    },
    updatedEditButtons() {
      const isEditableState =
        this.invoiceState === 'Sin Enviar' ||
        this.invoiceState === 'En Transito';

      return [
        {
          icon: iconPencil,
          cssClass:
            'grid-button-icon samples-list-details-edit-sample-button',
          onClick: this.viewAtc,
          visible: isEditableState,
          // disabled:
          //   this.invoiceState !== 'Sin Enviar' &&
          //   this.invoiceState !== 'En Transito',
        },
        {
          icon: iconTrash,
          cssClass: 'grid-button-icon',
          onClick: this.deleteAtc,
          visible: this.invoiceState === 'Sin Enviar',
        },
      ];
    },
    ...mapState('samplesRegisterModule', {
      showSplPopup: 'showSplPopup',
      title: 'splPopupTitle',
      invoiceState: 'invoiceState',
      families: 'families',
    }),
    ...mapState('Offers', [
      'detallesPresupuestos',
      'analisisTipoDescrip',
    ]),
    ...mapGetters('contact', ['contactInfo']),
  },
  watch: {
    store: {
      async handler() {
        this.loaded = false;
        await this.enrichAnalysisDisplaySamples();
        this.loaded = true;
      },
      deep: true,
    },
  },
  async mounted() {
    try {
      this.$f7.preloader.show();
      this.loaded = false;
      await this.enrichAnalysisDisplaySamples();
    } catch (error) {
      this.$notifyDX(
        {
          message: this.$t(
            this.$helpers.getFilteredErrorMessage(
              JSON.parse(error.message).error
            )
          ),
          width: 550,
        },
        'error'
      );
    } finally {
      setTimeout(() => {
        this.$f7.preloader.hide();
      }, 0);
      this.loaded = true;
    }
  },
  methods: {
    getTranslatedSubFamilyLabel(item) {
      const familyId = Number(item?.IdFamilia ?? item?.idFamiliaTM);
      const subFamilyId = Number(
        item?.IdSubFamilia ??
          item?.IdSubFamiliaTM ??
          item?.idSubFamiliaTM
      );
      const family = (this.families || []).find(
        (entry) => Number(entry?.idFamiliaTM) === familyId
      );
      const subFamily = family?.SubFamiliasTM?.find(
        (entry) => Number(entry?.idSubFamiliaTM) === subFamilyId
      );

      return subFamily?.SubFamiliaTM ?? item?.SubFamiliaTM ?? '';
    },
    async enrichAnalysisDisplaySamples() {
      const samples = Array.isArray(this.store) ? this.store : [];
      if (samples.length === 0) {
        this.enrichedSamples = [];
        return;
      }

      if (
        !Array.isArray(this.families) ||
        this.families.length === 0
      ) {
        await this.fetchFamiliesByHierarchy();
      }

      const detailsByBudgetAndArticle = new Map();
      const budgetCodes = [
        ...new Set(
          samples
            .map((sample) => sample?.CodPresupuesto)
            .filter(
              (budgetCode) =>
                budgetCode !== null &&
                budgetCode !== undefined &&
                String(budgetCode).trim() !== ''
            )
        ),
      ];

      // Recorremos los presupuestos en serie porque las acciones mutan
      // estado compartido de Vuex y necesitamos conservar ese contexto.
      await budgetCodes.reduce(
        async (previousBudgetPromise, budgetCode) => {
          await previousBudgetPromise;
          await this.fetchDetallesPresupuestos(budgetCode);
          const offerLines = Array.isArray(this.detallesPresupuestos)
            ? [...this.detallesPresupuestos]
            : [];
          const analysisLines =
            await this.buildOfferLinesWithAtDescrip(offerLines);

          analysisLines.forEach((line) => {
            const key = `${budgetCode}::${line.CodArticulo}`;
            if (!detailsByBudgetAndArticle.has(key)) {
              detailsByBudgetAndArticle.set(key, line);
            }
          });
        },
        Promise.resolve()
      );

      this.enrichedSamples = samples.map((sample) => {
        const key = `${sample?.CodPresupuesto}::${sample?.CodArticulo}`;
        const detail = detailsByBudgetAndArticle.get(key);

        return detail
          ? {
              ...sample,
              Articulo: detail.Articulo ?? sample.Articulo,
              DescripcionAT:
                detail.DescripcionAT ?? sample.DescripcionAT,
              SubFamiliaTM:
                detail.SubFamiliaTM ?? sample.SubFamiliaTM,
              AtDescrip: detail.AtDescrip ?? sample.AtDescrip,
              IdFamilia: detail.IdFamilia ?? sample.IdFamilia,
              IdSubFamilia:
                detail.IdSubFamilia ?? sample.IdSubFamilia,
              IdSubFamiliaTM:
                detail.IdSubFamiliaTM ?? sample.IdSubFamiliaTM,
            }
          : sample;
      });
    },
    async buildOfferLinesWithAtDescrip(offerLines) {
      const atMap = new Map();
      let lastCombinationKey = null;
      let listedAnalysisType = this.analisisTipoDescrip;
      const budgetClientCode =
        this.detallesPresupuestos?.codCliente ||
        this.contactInfo?.company ||
        null;

      const sortedLines = [...offerLines].sort((a, b) => {
        if (a.IdFamilia !== b.IdFamilia)
          return a.IdFamilia - b.IdFamilia;
        return a.IdSubFamilia - b.IdSubFamilia;
      });

      await sortedLines.reduce(async (previousLinePromise, item) => {
        await previousLinePromise;
        const { IdFamilia, IdSubFamilia } = item;
        const codPlanta =
          item.CodPlantaCentroProduccionSeleccionada || '';
        const combinationKey = `${codPlanta}|${IdFamilia}|${IdSubFamilia}`;

        if (combinationKey !== lastCombinationKey) {
          lastCombinationKey = combinationKey;
          await this.fetchAnalisisTipo({
            codPlanta,
            idFamiliaTM: IdFamilia,
            idSubFamiliaTM: IdSubFamilia,
            codCliente: budgetClientCode,
            codIdioma: null,
          });
          listedAnalysisType = this.analisisTipoDescrip;
          (this.analisisTipoDescrip || []).forEach((analysis) => {
            if (
              analysis?.CodArticulo &&
              !atMap.has(analysis.CodArticulo)
            ) {
              atMap.set(analysis.CodArticulo, analysis);
            }
          });
        }

        let analysis = null;
        if (item.IdSubFamiliaTM === 63) {
          analysis = listedAnalysisType.find(
            (entry) => entry.AnalisisTipo === item.Articulo
          );
        } else {
          const analysisTypeId = item.CodArticulo?.slice(1);
          analysis = listedAnalysisType.find(
            (entry) =>
              entry.IdAnalisisTipo?.toString() === analysisTypeId
          );
        }

        if (analysis) {
          item.CodAnalisisTipo = analysis.CodAnalisisTipo;
          item.DescripcionAT =
            analysis.IdSubFamiliaTM === 63
              ? analysis.CodAnalisisTipo
              : analysis.DescripcionAT;
        }
      }, Promise.resolve());

      return sortedLines.map((item) => {
        const analysis = atMap.get(item.CodArticulo);
        const subFamilyDescription =
          this.getTranslatedSubFamilyLabel(item);

        return {
          ...item,
          AtDescrip: `${item.Articulo} - ${
            analysis?.DescripcionAT ?? item.DescripcionAT ?? ''
          } - ${subFamilyDescription}`,
        };
      });
    },
    buildAnalysisDisplayText(sample) {
      if (!sample) return '';
      if (sample.AtDescrip) return sample.AtDescrip;

      const article =
        sample.Articulo ||
        sample.CodArticulo ||
        sample.CodAnalisisTipo ||
        sample.AnalisisTipo ||
        '';
      const description = sample.DescripcionAT || '';
      const subFamily =
        sample.SubFamiliaTM || sample.SubFamilia || '';

      const parts = [article, description, subFamily].filter(
        (part) => typeof part === 'string' && part.trim() !== ''
      );

      return parts.length ? parts.join(' - ') : '';
    },
    getSampleCurrency(sample) {
      return helpers.resolveCurrency({
        conversionX3: sample?.ConversionX3,
        clientCurrency: this.contactInfo?.currency,
        m: sample?.M,
        moneda: sample?.Moneda,
        fallback: this.currency || 'EUR',
      });
    },
    formatAmount(amount, currency) {
      if (amount === null || amount === undefined || amount === '') {
        return '';
      }

      const parsedAmount = Number(
        typeof amount === 'string'
          ? amount.replace(',', '.').replace(/[^\d.-]/g, '')
          : amount
      );

      if (Number.isNaN(parsedAmount)) {
        return String(amount);
      }

      return helpers.formatCurrency(parsedAmount, currency, 'es');
    },
    viewAtc(e) {
      this.setDetailedSample(e.row.data);
      this.$f7router.navigate(
        {
          name: 'SamplesDetails',
        },
        {
          animate: true,
          transition: 'f7-fade',
        }
      );
    },
    deleteAtc(e) {
      try {
        const dataGrid = e.component;
        dataGrid.deleteRow(e.row.rowIndex);
        this.deleteSample(e.row.data.IdWMuestra);
        this.$f7.preloader.show();
      } catch (error) {
        this.$notifyDX(
          {
            message: this.$t(
              this.$helpers.getFilteredErrorMessage(error.message)
            ),
            width: 550,
          },
          'error',
          NOTIFY_TIME_LONG
        );
      } finally {
        this.$f7.preloader.hide();
      }
    },
    init(e) {
      this.grid = e;
      Array.from(
        document.getElementsByClassName('dx-group-cell')
      ).forEach((el) => {
        el.innerHTML = el.innerHTML.split(':')[1].trim();
      });
    },
    hide() {
      this.setSplpopup(false);
    },
    ...mapActions('Offers', [
      'fetchDetallesPresupuestos',
      'fetchAnalisisTipo',
    ]),
    ...mapActions('samplesRegisterModule', [
      'fetchFamiliesByHierarchy',
      'setSplpopup',
      'setDetailedSample',
      'deleteSample',
    ]),
  },
};
</script>

<style scoped>
.samples-list-details-grid {
  position: relative;
}

.preloader-overlay {
  position: absolute;
  inset: 0;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.6);
  backdrop-filter: blur(3px);
  pointer-events: all;
}

.preloader-card {
  min-width: 180px;
  min-height: 88px;
  padding: 14px 18px;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.preloader-text {
  font-size: 14px;
  font-weight: 500;
  color: #4b5563;
}

.invoice-info-container {
  padding: 20px;
  background-color: #f9f9f9;
  border-radius: 8px;
}

.styled-list {
  list-style-type: none;
  padding-left: 0;
  margin: 0;
}

.styled-list li {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #e5e4e7;
  color: #333;
}

.styled-list li:last-child {
  border-bottom: none;
}

.label {
  font-weight: bold;
  color: #666;
}
</style>
